import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  CircularProgress,
  Chip,
  useTheme,
  useMediaQuery,
  Stack,
  InputAdornment,
  Tabs,
  Tab,
  Menu,
  ListItemIcon,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import Search from '@mui/icons-material/Search';
import ContentCopy from '@mui/icons-material/ContentCopy';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import MoreVert from '@mui/icons-material/MoreVert';
import People from '@mui/icons-material/People';
import { useSnackbar } from 'notistack';
import { fetchUsers, createUser, deleteUser, clearLastCreated } from '../../store/slices/usersSlice';
import { DashboardPageSkeleton } from '../../components/Dashboard/DashboardPageSkeleton';

const ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'moderator', label: 'Modérateur' },
  { value: 'professor', label: 'Professeur' },
  { value: 'student', label: 'Étudiant' },
  { value: 'space-student', label: 'Espace étudiant' },
];

const ROLE_COLORS = {
  admin: 'error',
  moderator: 'warning',
  professor: 'info',
  student: 'success',
  'space-student': 'default',
};

const ORGANIZATION_NAME = 'Université de Ngozi (UNG) - Burundi';

function formatJoined(date) {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'numeric', year: 'numeric' });
}

export default function Users() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const { list: users, loading, lastCreated } = useSelector((s) => s.users);
  const [openCreate, setOpenCreate] = useState(false);
  const [openCredentials, setOpenCredentials] = useState(false);
  const [form, setForm] = useState({ fullname: '', email: '', position: '', role: 'student' });
  const [createdCreds, setCreatedCreds] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null, password: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuUser, setMenuUser] = useState(null);
  const { user } = useSelector((s) => s.auth);

  const filteredUsers = users.filter((u) => {
    const q = searchQuery.toLowerCase().trim();
    const matchSearch = !q || [u.fullname, u.email, u.position].some((v) => v && String(v).toLowerCase().includes(q));
    const matchRole = roleFilter === 'all' || u.role === roleFilter;
    return matchSearch && matchRole;
  });

  useEffect(() => {
    if (user?.role === 'admin') dispatch(fetchUsers());
  }, [dispatch, user?.role]);

  useEffect(() => {
    if (lastCreated?.user && lastCreated?.temporaryPassword) {
      setCreatedCreds({ email: lastCreated.user.email, password: lastCreated.temporaryPassword });
      setOpenCreate(false);
      setOpenCredentials(true);
      enqueueSnackbar("Un e-mail a été envoyé à l'utilisateur avec ses identifiants.", { variant: 'success' });
      dispatch(clearLastCreated());
    }
  }, [lastCreated, enqueueSnackbar, dispatch]);

  if (user?.role !== 'admin') return <Navigate to="/dashboard" replace />;

  if (loading && users.length === 0) {
    return <DashboardPageSkeleton variant="table" />;
  }

  const handleCreate = async (e) => {
    e.preventDefault();
    const result = await dispatch(createUser(form));
    if (createUser.rejected.match(result)) {
      enqueueSnackbar(result.payload || 'Erreur.', { variant: 'error' });
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      enqueueSnackbar('Copié dans le presse-papiers.', { variant: 'success' });
    });
  };

  const handleOpenDelete = (u) => {
    setDeleteDialog({ open: true, user: u, password: '' });
  };

  const handleCloseDelete = () => {
    setDeleteDialog({ open: false, user: null, password: '' });
  };

  const handleConfirmDelete = async (e) => {
    e.preventDefault();
    if (!deleteDialog.user || !deleteDialog.password.trim()) {
      enqueueSnackbar('Veuillez entrer votre mot de passe pour confirmer.', { variant: 'error' });
      return;
    }
    const result = await dispatch(deleteUser({ id: deleteDialog.user._id, password: deleteDialog.password }));
    if (deleteUser.fulfilled.match(result)) {
      enqueueSnackbar('Utilisateur supprimé avec succès.', { variant: 'success' });
      handleCloseDelete();
    } else {
      enqueueSnackbar(result.payload || 'Erreur lors de la suppression.', { variant: 'error' });
    }
  };

  return (
    <Box sx={{ width: '100%', overflow: 'hidden', position: 'relative' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold" color="text.primary" gutterBottom>
            Gestion d'équipe
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gérez les membres de l'équipe et leurs contenus sur la plateforme.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setOpenCreate(true)}
          sx={{ textTransform: 'none', fontWeight: 600, px: 2 }}
        >
          Créer un utilisateur
        </Button>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems="center">
        <TextField
          size="small"
          fullWidth
          placeholder="Rechercher par nom, e-mail ou organisation…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ maxWidth: 400 }}
        />
        <TextField
          select
          size="small"
          label="Rôle"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="all">Tous les rôles</MenuItem>
          {ROLES.map((r) => (
            <MenuItem key={r.value} value={r.value}>
              {r.label}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Tabs value={0} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label={`Utilisateurs (${users.length})`} id="users-tab" />
        <Tab
          label="Tous les articles"
          id="posts-tab"
          onClick={() => navigate('/dashboard/posts')}
        />
      </Tabs>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
          bgcolor: 'background.paper',
        }}
      >
        <TableContainer
          sx={{
            maxHeight: { xs: 'calc(100vh - 280px)', sm: 'calc(100vh - 260px)', md: 520 },
            overflowX: 'auto',
            overflowY: 'auto',
          }}
        >
          <Table stickyHeader size={isSmall ? 'small' : 'medium'} sx={{ minWidth: 640 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: theme.palette.mode === 'light' ? 'grey.50' : 'grey.900' }}>
                <TableCell sx={{ fontWeight: 600, py: 2, borderBottom: 2, borderColor: 'divider' }}>Utilisateur</TableCell>
                <TableCell sx={{ fontWeight: 600, py: 2, borderBottom: 2, borderColor: 'divider' }}>Organisation</TableCell>
                <TableCell sx={{ fontWeight: 600, py: 2, borderBottom: 2, borderColor: 'divider' }}>Rôle</TableCell>
                <TableCell sx={{ fontWeight: 600, py: 2, borderBottom: 2, borderColor: 'divider' }}>Articles</TableCell>
                <TableCell sx={{ fontWeight: 600, py: 2, borderBottom: 2, borderColor: 'divider' }}>Inscrit le</TableCell>
                <TableCell sx={{ fontWeight: 600, py: 2, borderBottom: 2, borderColor: 'divider', width: 72 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">
                      {users.length === 0 ? 'Aucun utilisateur pour le moment.' : 'Aucun résultat pour cette recherche.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((u) => {
                  const count = u.postCount ?? 0;
                  return (
                    <TableRow
                      key={u._id}
                      sx={{
                        '&:hover': { bgcolor: theme.palette.action.hover },
                        transition: 'background-color 0.2s',
                      }}
                    >
                      <TableCell sx={{ py: 2 }}>
                        <Box>
                          <Typography variant="body2" fontWeight="600">{u.fullname}</Typography>
                          <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Box>
                          <Typography variant="body2">{ORGANIZATION_NAME}</Typography>
                          <Typography variant="caption" color="text.secondary">{u.position}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Chip
                          label={ROLES.find((r) => r.value === u.role)?.label || u.role}
                          color={ROLE_COLORS[u.role] || 'default'}
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body2">{count} article{count !== 1 ? 's' : ''}</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body2">{formatJoined(u.createdAt)}</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <IconButton
                          size="small"
                          onClick={(e) => { setMenuAnchor(e.currentTarget); setMenuUser(u); }}
                          aria-label="Actions"
                        >
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      {loading && users.length > 0 && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(255,255,255,0.75)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            zIndex: 1,
            borderRadius: 2,
            animation: 'overlayFadeIn 0.2s ease-out',
            '@keyframes overlayFadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
          }}
        >
          <CircularProgress size={40} thickness={4} sx={{ color: 'primary.main' }} />
          <Typography variant="body2" fontWeight="500" color="text.secondary">
            Chargement…
          </Typography>
        </Box>
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => { setMenuAnchor(null); setMenuUser(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            if (menuUser) handleOpenDelete(menuUser);
            setMenuAnchor(null);
            setMenuUser(null);
          }}
          disabled={menuUser?._id === user?._id}
        >
          <ListItemIcon><DeleteOutline fontSize="small" /></ListItemIcon>
          Supprimer
        </MenuItem>
      </Menu>

      <Dialog
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}
      >
        <form onSubmit={handleCreate}>
          <DialogTitle sx={{ fontWeight: 600, fontSize: '1.25rem' }}>Créer un utilisateur</DialogTitle>
          <DialogContent sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Nom complet"
              value={form.fullname}
              onChange={(e) => setForm((f) => ({ ...f, fullname: e.target.value }))}
              required
              margin="normal"
              variant="outlined"
            />
            <TextField
              fullWidth
              label="E-mail"
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              required
              margin="normal"
              variant="outlined"
            />
            <TextField
              fullWidth
              label="Organisation / Fonction"
              value={form.position}
              onChange={(e) => setForm((f) => ({ ...f, position: e.target.value }))}
              required
              margin="normal"
              variant="outlined"
            />
            <TextField
              fullWidth
              select
              label="Rôle"
              value={form.role}
              onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
              margin="normal"
              variant="outlined"
            >
              {ROLES.map((r) => (
                <MenuItem key={r.value} value={r.value}>
                  {r.label}
                </MenuItem>
              ))}
            </TextField>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
            <Button onClick={() => setOpenCreate(false)} disabled={loading} sx={{ textTransform: 'none' }}>
              Annuler
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {loading ? 'Création en cours…' : 'Créer l\'utilisateur'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={openCredentials}
        onClose={() => {
          setOpenCredentials(false);
          setCreatedCreds(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Identifiants créés</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Copiez ces informations pour les transmettre à l'utilisateur. Un e-mail lui a également été envoyé.
          </Typography>
          {createdCreds && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="body2">
                  <strong>E-mail :</strong> {createdCreds.email}
                </Typography>
                <IconButton size="small" onClick={() => copyToClipboard(createdCreds.email)}>
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">
                  <strong>Mot de passe :</strong> {createdCreds.password}
                </Typography>
                <IconButton size="small" onClick={() => copyToClipboard(createdCreds.password)}>
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenCredentials(false);
              setCreatedCreds(null);
            }}
            variant="contained"
          >
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialog.open}
        onClose={handleCloseDelete}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <form onSubmit={handleConfirmDelete}>
          <DialogTitle sx={{ color: 'error.main', fontWeight: 600 }}>
            Confirmer la suppression
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Êtes-vous sûr de vouloir supprimer <strong>{deleteDialog.user?.fullname}</strong> ({deleteDialog.user?.email}) ?
              Cette action est irréversible.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Entrez votre mot de passe pour confirmer :
            </Typography>
            <TextField
              fullWidth
              type="password"
              placeholder="Votre mot de passe"
              value={deleteDialog.password}
              onChange={(e) => setDeleteDialog((d) => ({ ...d, password: e.target.value }))}
              autoComplete="current-password"
              size="small"
              margin="dense"
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCloseDelete} disabled={loading}>
              Annuler
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="error"
              disabled={loading || !deleteDialog.password.trim()}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <DeleteOutline />}
            >
              {loading ? 'Suppression…' : 'Supprimer'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}
